import {
  DataSourceStatus,
  type DealItemWithVersions,
  type ValidationError,
  type ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { dealAtom } from '@/states/deal.state';
import { effectiveValidationErrorsAtom } from '@/states/effective-validation-errors.state';
import { pendingOptimisticUpdatesState } from '@/states/pending-optimistic-updates.state';
import {
  dealItemsLoadingState,
  dealItemsStreamingState,
  productsState,
} from '@/states/products.state';
import { atom } from 'jotai';

import { CSV_STEPS, type CsvStepId, stepsOfError } from './steps';

export const VALIDATION_PENDING_FIELD = 'validationErrors';
export const VAT_PENDING_FIELD = 'vatCoherence';

const FIELDS_SUSPENDING_VALIDATION = [
  VALIDATION_PENDING_FIELD,
  'vatRate',
  'productCategory',
];

type Severity = 'error' | 'warning';

export type CsvRuleSummary = {
  ruleId: ValidationRuleId;
  severity: Severity;
  itemIds: string[];
};

export type CsvStepSummary = {
  errors: number;
  warnings: number;
  itemIds: string[];
  byRule: CsvRuleSummary[];
};

export type CsvStepErrorsByItem = Map<string, ValidationError[]>;

const toSeverity = (severity: string): Severity | null =>
  severity === 'error' || severity === 'warning' ? severity : null;

export const csvProductsAtom = atom((get) =>
  get(productsState).filter((product) => product.activeVersion.isSelected)
);

export const csvProductsLoadedAtom = atom(
  (get) =>
    !get(dealItemsLoadingState).initialLoading && !get(dealItemsStreamingState)
);

export const csvErrorsByStepAtom = atom((get) => {
  const effectiveErrors = get(effectiveValidationErrorsAtom);
  const byStep = new Map<CsvStepId, CsvStepErrorsByItem>(
    CSV_STEPS.map((step) => [step, new Map()])
  );

  for (const product of get(csvProductsAtom)) {
    const errors = effectiveErrors.get(product.id);
    if (!errors) continue;
    for (const error of errors) {
      if (!toSeverity(error.severity)) continue;
      for (const step of stepsOfError(error, product.activeVersion)) {
        const itemErrors = byStep.get(step)!;
        const list = itemErrors.get(product.id);
        if (list) list.push(error);
        else itemErrors.set(product.id, [error]);
      }
    }
  }

  return byStep;
});

const summarize = (itemErrors: CsvStepErrorsByItem): CsvStepSummary => {
  let errors = 0;
  let warnings = 0;
  const rules = new Map<ValidationRuleId, CsvRuleSummary>();

  for (const [itemId, list] of itemErrors) {
    const seen = new Set<ValidationRuleId>();
    for (const error of list) {
      if (seen.has(error.ruleId)) continue;
      seen.add(error.ruleId);
      const severity = toSeverity(error.severity)!;
      if (severity === 'error') errors++;
      else warnings++;
      const rule = rules.get(error.ruleId);
      if (rule) {
        rule.itemIds.push(itemId);
        if (severity === 'error') rule.severity = 'error';
      } else {
        rules.set(error.ruleId, {
          ruleId: error.ruleId,
          severity,
          itemIds: [itemId],
        });
      }
    }
  }

  return {
    errors,
    warnings,
    itemIds: [...itemErrors.keys()],
    byRule: [...rules.values()],
  };
};

export const csvStepSummariesAtom = atom((get) => {
  const byStep = get(csvErrorsByStepAtom);
  return Object.fromEntries(
    CSV_STEPS.map((step) => [step, summarize(byStep.get(step)!)])
  ) as Record<CsvStepId, CsvStepSummary>;
});

export const csvRuleCountAtom = atom((get) => {
  const summaries = get(csvStepSummariesAtom);
  return new Set(
    CSV_STEPS.flatMap((step) => summaries[step].byRule.map((r) => r.ruleId))
  ).size;
});

export const csvItemIdsBySeverityAtom = atom((get) => {
  const bySeverity: Record<Severity, Set<string>> = {
    error: new Set(),
    warning: new Set(),
  };
  for (const itemErrors of get(csvErrorsByStepAtom).values()) {
    for (const [itemId, list] of itemErrors) {
      for (const error of list) {
        bySeverity[toSeverity(error.severity)!].add(itemId);
      }
    }
  }
  return bySeverity;
});

const hasAnyField = (product: DealItemWithVersions, fields: string[]) =>
  product.activeVersion.updatingFields?.some((field) =>
    fields.includes(field)
  ) ?? false;

export type CsvAwaitingServer = Map<
  string,
  { historyId: string | null | undefined; until: number; held?: boolean }
>;

export const csvAwaitingServerAtom = atom<CsvAwaitingServer>(new Map());

export const CSV_AWAITING_SERVER_MS = 15_000;

export const CSV_AWAITING_HELD_MS = 5 * 60_000;

export const CSV_RECHECK_SHOWN_AFTER_MS = 1_500;

export const markCsvAwaitingServerAtom = atom(
  null,
  (
    get,
    set,
    {
      itemIds,
      now = Date.now(),
      held = false,
    }: { itemIds: string[]; now?: number; held?: boolean }
  ) => {
    const products = get(productsState);
    const byId = new Map(products.map((product) => [product.id, product]));
    set(csvAwaitingServerAtom, (prev) => {
      const next = new Map(prev);
      for (const id of itemIds) {
        next.set(id, {
          historyId: byId.get(id)?.activeVersion.historyId,
          until: now + (held ? CSV_AWAITING_HELD_MS : CSV_AWAITING_SERVER_MS),
          held,
        });
      }
      return next;
    });
  }
);

export const settleCsvAwaitingServerAtom = atom(
  null,
  (
    _get,
    set,
    { itemIds, now = Date.now() }: { itemIds: string[]; now?: number }
  ) => {
    set(csvAwaitingServerAtom, (prev) => {
      if (!itemIds.some((id) => prev.get(id)?.held)) return prev;
      const next = new Map(prev);
      for (const id of itemIds) {
        const entry = prev.get(id);
        if (entry?.held)
          next.set(id, {
            ...entry,
            held: false,
            until: now + CSV_AWAITING_SERVER_MS,
          });
      }
      return next;
    });
  }
);

export const releaseCsvAwaitingServerAtom = atom(
  null,
  (_get, set, itemIds: Iterable<string>) => {
    const ids = [...itemIds];
    if (ids.length === 0) return;
    set(csvAwaitingServerAtom, (prev) => {
      const next = new Map(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }
);

export const pruneCsvAwaitingServerAtom = atom(
  null,
  (get, set, now: number = Date.now()) => {
    const awaiting = get(csvAwaitingServerAtom);
    if (awaiting.size === 0) return;
    const byId = new Map(
      get(productsState).map((product) => [product.id, product])
    );
    let changed = false;
    const next = new Map(awaiting);
    for (const [id, entry] of awaiting) {
      const current = byId.get(id)?.activeVersion.historyId;
      if (
        now >= entry.until ||
        !byId.has(id) ||
        (!entry.held && current !== entry.historyId)
      ) {
        next.delete(id);
        changed = true;
      }
    }
    if (changed) set(csvAwaitingServerAtom, next);
  }
);

export const csvPendingItemIdsAtom = atom((get) => {
  const effectiveErrors = get(effectiveValidationErrorsAtom);
  const inFlight = get(pendingOptimisticUpdatesState);
  const awaiting = get(csvAwaitingServerAtom);
  const pending = new Set<string>();

  for (const product of get(csvProductsAtom)) {
    if (
      effectiveErrors.get(product.id) === null ||
      hasAnyField(product, FIELDS_SUSPENDING_VALIDATION) ||
      inFlight.has(product.id) ||
      (awaiting.has(product.id) &&
        (awaiting.get(product.id)!.held ||
          awaiting.get(product.id)!.historyId ===
            product.activeVersion.historyId))
    ) {
      pending.add(product.id);
    }
  }

  return pending;
});

const ENRICHED_FIELD_STEPS: Record<string, CsvStepId[]> = {
  productCategory: ['tva'],
  vatRate: ['tva', 'prix'],
};

export const VAT_JUDGE_STEPS: readonly CsvStepId[] = ['tva', 'prix'];

export const csvVerificationPendingStepsAtom = atom((get) => {
  if (!get(csvProductsLoadedAtom) || get(csvImportInProgressAtom))
    return new Set<CsvStepId>(CSV_STEPS);

  const pending = get(csvPendingItemIdsAtom);
  const steps = new Set<CsvStepId>();
  for (const product of get(csvProductsAtom)) {
    if (!pending.has(product.id)) continue;
    const enrichedSteps = Object.entries(ENRICHED_FIELD_STEPS)
      .filter(([field]) => hasAnyField(product, [field]))
      .flatMap(([, fieldSteps]) => fieldSteps);
    const onlyEnriching =
      enrichedSteps.length > 0 &&
      !product.activeVersion.updatingFields?.includes(
        VALIDATION_PENDING_FIELD
      ) &&
      get(effectiveValidationErrorsAtom).get(product.id) != null &&
      !get(pendingOptimisticUpdatesState).has(product.id) &&
      !get(csvAwaitingServerAtom).has(product.id);
    for (const step of onlyEnriching ? enrichedSteps : CSV_STEPS) {
      steps.add(step);
    }
    if (steps.size === CSV_STEPS.length) break;
  }
  if (get(csvVatPendingItemIdsAtom).size > 0) {
    for (const step of VAT_JUDGE_STEPS) steps.add(step);
  }
  return steps;
});

export const csvVatPendingItemIdsAtom = atom((get) => {
  const pending = new Set<string>();
  for (const product of get(csvProductsAtom)) {
    if (hasAnyField(product, [VAT_PENDING_FIELD])) pending.add(product.id);
  }
  return pending;
});

export const csvEditedStepsByItemAtom = atom<Map<string, Set<CsvStepId>>>(
  new Map()
);

export const markCsvItemsEditedAtom = atom(
  null,
  (_get, set, { itemIds, step }: { itemIds: string[]; step: CsvStepId }) => {
    set(csvEditedStepsByItemAtom, (prev) => {
      const next = new Map(prev);
      for (const id of itemIds) {
        next.set(id, new Set([...(next.get(id) ?? []), step]));
      }
      return next;
    });
  }
);

const IMPORT_RUNNING_STATUSES = new Set<DataSourceStatus>([
  DataSourceStatus.Pending,
  DataSourceStatus.Imported,
]);

export const csvValidationFailedAtom = atom(false);

type DealDataSource = NonNullable<
  NonNullable<ReturnType<typeof dealAtom.read>>['dataSources']
>[number];

const createdAtMs = (source: DealDataSource): number =>
  source.createdAt ? new Date(source.createdAt).getTime() : 0;

export const csvAwaitedImportAtom = atom<{
  knownDataSourceIds: string[];
} | null>(null);

const latestCsvSourceOf = (
  dataSources: readonly DealDataSource[]
): DealDataSource | null =>
  dataSources
    .filter((source) => source.__typename === 'CSVDataSource')
    .reduce<DealDataSource | null>(
      (acc, source) =>
        !acc || createdAtMs(source) > createdAtMs(acc) ? source : acc,
      null
    );

export const csvImportInProgressAtom = atom((get) => {
  const deal = get(dealAtom);
  if (!deal) return true;
  const dataSources = deal.dataSources ?? [];

  const awaited = get(csvAwaitedImportAtom);
  if (
    awaited &&
    !dataSources.some(
      (source) => !awaited.knownDataSourceIds.includes(source.id)
    )
  ) {
    return true;
  }

  return dataSources.some(
    (source) =>
      source.status != null && IMPORT_RUNNING_STATUSES.has(source.status)
  );
});

export const csvLatestImportAtom = atom((get) =>
  latestCsvSourceOf(get(dealAtom)?.dataSources ?? [])
);

export const csvLastImportFailedAtom = atom((get) => {
  if (get(csvImportInProgressAtom)) return false;
  return get(csvLatestImportAtom)?.status === DataSourceStatus.Error;
});

export const csvBlockingPendingStepsAtom = atom((get) => {
  const steps = new Set<CsvStepId>();
  if (!get(csvProductsLoadedAtom) || get(csvImportInProgressAtom))
    return new Set<CsvStepId>(CSV_STEPS);

  const pending = get(csvPendingItemIdsAtom);
  const edited = get(csvEditedStepsByItemAtom);
  const byStep = get(csvErrorsByStepAtom);

  for (const id of pending) {
    for (const step of edited.get(id) ?? []) steps.add(step);
    for (const step of CSV_STEPS) {
      if (byStep.get(step)!.has(id)) steps.add(step);
    }
  }
  return steps;
});

export const csvPendingStepsAtom = atom((get) => {
  const steps = new Set(get(csvBlockingPendingStepsAtom));
  if (get(csvVatPendingItemIdsAtom).size > 0) steps.add('tva');
  return steps;
});

export const csvVerificationReadyAtom = atom((get) => {
  if (get(csvValidationFailedAtom)) return false;
  if (get(dealItemsLoadingState).initialLoading) return false;
  if (get(dealItemsStreamingState)) return false;
  if (get(csvImportInProgressAtom)) return false;
  return get(csvPendingItemIdsAtom).size === 0;
});

export const csvFullyVerifiedAtom = atom(
  (get) =>
    get(csvVerificationReadyAtom) && get(csvVatPendingItemIdsAtom).size === 0
);

export const csvCanProceedToClosingAtom = atom((get) => {
  if (!get(csvVerificationReadyAtom)) return false;
  if (get(csvProductsAtom).length === 0) return false;
  if (get(csvBlockingPendingStepsAtom).size > 0) return false;
  const summaries = get(csvStepSummariesAtom);
  return CSV_STEPS.every((step) => summaries[step].errors === 0);
});
